// src/pages/Downpipes.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MediaCarousel from "../components/MediaCarousel";
import SearchFilters from "../components/SearchFilters";
import { DOWNPIPE_PRODUCTS } from "../data/downpipeProducts";
import { ALL_PRODUCTS } from "../data/exhaustProducts"; // only for filter options

// Loads sequential JPGs from /public/assets/dp_clip01.jpg, dp_clip02.jpg, ...
async function loadSequentialImages({ base = "/assets/dp_clip", start = 1, end = 50, pad = 2, ext = ".jpg" }) {
  const exists = async (url) => {
    try {
      const res = await fetch(url, { method: "HEAD" });
      return res.ok;
    } catch {
      return false;
    }
  };
  const found = [];
  for (let i = start; i <= end; i++) {
    const num = String(i).padStart(pad, "0");
    const url = `${base}${num}${ext}`;
    if (await exists(url)) found.push({ id: `i${i}`, type: "image", src: url, name: `Image ${i}` });
  }
  return found;
}

// Safe currency formatter (handles numbers or "$1,234" style strings)
function formatMoney(val) {
  if (val == null) return null;
  if (typeof val === "string") {
    // If it already looks like money, return as-is
    if (/\$/.test(val)) return val;
    const num = Number(val.replace(/[^\d.]/g, ""));
    if (!isNaN(num)) return `$${num.toLocaleString()}`;
    return val; // fallback
  }
  if (typeof val === "number") return `$${val.toLocaleString()}`;
  return null;
}

// Pull the first defined price-like field
function pickPrice(p) {
  const priceCandidate =
    p?.startingAt ??
    p?.basePrice ??
    p?.price ??
    p?.fromPrice ??
    null;

  const formatted = formatMoney(priceCandidate);
  if (!formatted) return null;

  // Choose a friendly label if available
  const label =
    p?.priceLabel ||
    (p?.startingAt != null ? "Starting at" : "Price");

  return `${label} ${formatted}`;
}

export default function Downpipes() {
  // HERO + carousel media
  const [heroSrc, setHeroSrc] = useState("/assets/downpipe.jpg");
  const [carouselMedia, setCarouselMedia] = useState([]);

  // Make/Model filters (same API as Exhaust page)
  const [filters, setFilters] = useState({ make: "", model: "" });

  useEffect(() => {
    (async () => {
      const imgs = await loadSequentialImages({
        base: "/assets/dp_clip",
        start: 1,
        end: 50,
        pad: 2,
        ext: ".jpg",
      });
      setCarouselMedia(imgs);
    })();
  }, []);

  // Filter DOWNPIPE_PRODUCTS by selected Make/Model
  const filteredDownpipes = DOWNPIPE_PRODUCTS.filter((p) => {
    const byMake = !filters.make || p.make === filters.make;
    const byModel = !filters.model || p.model === filters.model;
    return byMake && byModel;
  });

  return (
    <section className="mx-auto max-w-7xl px-4 py-16 text-neutral-900">
      {/* ===== Top: hero left, text + carousel right ===== */}
      <div className="grid md:grid-cols-2 gap-8 items-start">
        {/* Left: main hero image */}
        <div className="rounded-2xl overflow-hidden border border-neutral-200 shadow-sm bg-black">
          <img
            key={heroSrc}
            src={heroSrc}
            alt="Unlimited Levels Downpipes"
            className="w-full h-[420px] object-cover"
          />
        </div>

        {/* Right: text + CTA + carousel */}
        <div>
          <h1 className="text-3xl font-bold mb-4">Unlimited Levels Downpipes</h1>
          <p className="text-lg text-neutral-700 mb-6">
            High-flow options engineered for spool and top-end power.
            Handcrafted with premium materials and TIG welds—built for daily driving and track use.
            Choose Stainless Steel for a deeper tone, or Titanium for that sharp F1-style sound.
          </p>

          {/* same quote form page as exhaust */}
          <Link
            to="/request-exhaust-quote"
            className="inline-flex items-center px-5 py-3 rounded-xl bg-black text-white font-semibold hover:bg-neutral-800 transition"
          >
            Request a Quote
          </Link>

          <div className="mt-10">
            <h3 className="text-xl font-semibold mb-3">Media</h3>
            <MediaCarousel
              items={carouselMedia}
              onSelect={(m) => {
                if (m.type === "image") setHeroSrc(m.src);
              }}
            />
          </div>
        </div>
      </div>

      {/* ===== Shop + Filters ===== */}
      <div className="mt-14">
        <h2 className="text-2xl font-bold mb-3">Shop</h2>

        {/* Populate dropdowns from ALL_PRODUCTS (exhaustProducts.js) */}
        <SearchFilters
          allProducts={ALL_PRODUCTS}
          value={filters}
          onChange={setFilters}
        />

        {/* Render DOWNPIPE_PRODUCTS (filtered) */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredDownpipes.map((p) => {
            const priceText = pickPrice(p); // ← price if present
            const subText = p?.car || p?.fitment || null;

            return (
              <div
                key={p.id}
                className="group relative bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden"
              >
                <img
                  src={p.img}
                  alt={p.name}
                  className="w-full h-48 object-cover transition duration-300 group-hover:scale-[1.03]"
                  onError={(e) => {
                    try {
                      const fallback = p.img?.replace("/assets/p-dp-", "/assets/p-exh-");
                      if (fallback && fallback !== p.img) e.currentTarget.src = fallback;
                    } catch {}
                  }}
                />

                <div className="p-4 flex flex-col gap-1">
                  <div className="font-semibold">{p.name}</div>
                  {subText && (
                    <div className="text-sm text-neutral-600">{subText}</div>
                  )}
                  {priceText && (
                    <div className="mt-2 font-bold">{priceText}</div>
                  )}
                </div>

                <Link
                  to={`/downpipes/${p.slug}`}
                  className="absolute bottom-3 right-3 px-3 py-2 rounded-lg bg-black text-white text-sm font-semibold hover:bg-neutral-800 transition"
                >
                  View
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
